import React, { useEffect, useState } from 'react';

import onCheckTokens from '@amzn/meridian-tokens/base/icon/check-circle';
import offCheckTokens from '@amzn/meridian-tokens/base/icon/close-circle';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DatePicker from '@amzn/meridian/date-picker';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import InputGroup from '@amzn/meridian/input-group';
import RadioButton from '@amzn/meridian/radio-button';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';

import { isEqual, uniqBy } from 'lodash';
import { TerritoryFlag } from 'src/components/TerritoryFlag';
import { GLOBAL_TERRITORY } from 'src/constants/context';
import {
    END_DATE_AFTER_START_DATE,
    IS_REQUIRED,
    MUST_GREATER_0_LESS_EQUAL_1,
    START_DATE_BEFORE_END_DATE
} from 'src/constants/errorMessages';
import {
    PLAN_TYPE_OPTIONS,
    PROMO_DURATION_OPTIONS,
    RENEWAL_CADENCE_OPTIONS,
    defaultPlanValues
} from 'src/constants/paperPlans';
import { AppContext } from 'src/contexts/appContext';
import { IPlanProps, IPlanStrValues } from 'src/types/IPaperPlan';
import { isPromoPlan } from 'src/util/PaperPlans/PaperPlanUtils';
import { dateComparator } from 'src/util/grid/sort';
import { validateNumberRange, validateRequired } from 'src/util/validationsUtil';

import './PlanDetails.scss';

const planOnIconWithLabel = (
    <Row spacing="200">
        <Icon className="on-check" tokens={onCheckTokens} />
        <Text className="on-check">On</Text>
    </Row>
);

const planOffIconWithLabel = (
    <Row spacing="200">
        <Icon className="off-check" tokens={offCheckTokens} />
        <Text className="off-check">Off</Text>
    </Row>
);

export const PlanDetails = ({
    onPrevious,
    onNext,
    onCancel,
    planValues,
    handlePlanValuesChange,
    setPlanValues
}: IPlanProps) => {
    const { metadata } = React.useContext(AppContext);
    const territories = Object.values(metadata.territoryMapping).filter(
        (territory) => territory.territoryCode !== GLOBAL_TERRITORY.NAME
    );

    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});
    const [isFormValid, setIsFormValid] = useState(false);

    const showPriceInputGroup = planValues.customerPays && planValues.royaltyBearing && planValues.carrierPlan;
    const showPromoMultiplierInput = isPromoPlan(planValues) && planValues.isBundle;

    useEffect(() => {
        setIsFormValid(checkFormValidity());
    }, [planValues, errorMessages]);

    useEffect(() => {
        if (planValues.isRenewalCadenceChecked) {
            setPlanValues((prevState) => ({
                ...prevState,
                promoDurationInDays: ''
            }));
            setErrorMessages((prevState) => {
                const newState = { ...prevState };
                delete newState['promoDurationInDays'];
                return newState;
            });
        } else {
            setPlanValues((prevState) => ({
                ...prevState,
                renewalCadence: ''
            }));
            setErrorMessages((prevState) => {
                const newState = { ...prevState };
                delete newState['renewalCadence'];
                return newState;
            });
        }
    }, [planValues.isRenewalCadenceChecked]);

    useEffect(() => {
        if (!showPriceInputGroup) {
            setPlanValues((prevState) => ({
                ...prevState,
                wholesalePrice: '',
                endUserPrice: '',
                wholesalePriceCurrency: '',
                endUserPriceCurrency: ''
            }));
        }
    }, [showPriceInputGroup]);

    useEffect(() => {
        if (!showPromoMultiplierInput) {
            setPlanValues((prevState) => ({
                ...prevState,
                promoMultiplier: '1'
            }));

            setErrorMessages((prevState) => {
                const newState = { ...prevState };
                delete newState['promoMultiplier'];
                return newState;
            });
        }
    }, [showPromoMultiplierInput]);

    const checkFormValidity = () => {
        const requiredFields = [
            'planType',
            'territory',
            'startDate',
            'renewalCadence',
            'promoDurationInDays',
            'wholesalePrice',
            'endUserPrice',
            'wholesalePriceCurrency',
            'endUserPriceCurrency',
            'promoMultiplier',
            'reportingName'
        ];

        for (const field of requiredFields) {
            if (!planValues[field as keyof IPlanStrValues]) {
                if (
                    !(
                        (field === 'renewalCadence' && !planValues.isRenewalCadenceChecked) ||
                        (field === 'promoDurationInDays' && planValues.isRenewalCadenceChecked) ||
                        (!showPriceInputGroup &&
                            [
                                'wholesalePrice',
                                'endUserPrice',
                                'wholesalePriceCurrency',
                                'endUserPriceCurrency'
                            ].includes(field)) ||
                        (!showPromoMultiplierInput && field === 'promoMultiplier')
                    )
                ) {
                    return false;
                }
            }
        }

        return isEqual(errorMessages, {}) && !isEqual(planValues, defaultPlanValues);
    };

    const onBlurField = (field: keyof IPlanStrValues) => {
        let errorMessage = '';
        const value = planValues[field];

        if (field === 'planType' || field === 'territory') {
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            }
        } else if (field === 'startDate') {
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            } else if (planValues.endDate && dateComparator(value, planValues.endDate) >= 0) {
                errorMessage = START_DATE_BEFORE_END_DATE;
            }
        } else if (field === 'endDate') {
            if (planValues.startDate && value && dateComparator(planValues.startDate, value) >= 0) {
                errorMessage = END_DATE_AFTER_START_DATE;
            }
        } else if (field === 'renewalCadence') {
            if (planValues.isRenewalCadenceChecked && !validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            }
        } else if (field === 'promoDurationInDays') {
            if (!planValues.isRenewalCadenceChecked && !validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            }
        } else if (field === 'wholesalePrice' || field === 'endUserPrice') {
            if (showPriceInputGroup && !validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            }
        } else if (field === 'wholesalePriceCurrency' || field === 'endUserPriceCurrency') {
            if (showPriceInputGroup && !validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            }
        } else if (field === 'promoMultiplier') {
            if (!validateRequired(value)) {
                errorMessage = IS_REQUIRED;
            } else if (!validateNumberRange(value ?? '', 0, false, 1, true)) {
                errorMessage = MUST_GREATER_0_LESS_EQUAL_1;
            }
        }

        setErrorMessages((prevState) => {
            const newState = { ...prevState };
            if (errorMessage === '') {
                delete newState[field];
            } else {
                newState[field] = errorMessage;
            }
            return newState;
        });
    };

    return (
        <>
            <Heading level={1}>Plan Details</Heading>

            <Column spacingInset="500 none none none" spacing="400">
                <Column spacing="200">
                    <Text type="h200">{planValues.paperPlanName}</Text>
                    <Text>Display name: {planValues.displayName}</Text>
                    <Text>Reporting name: {planValues.reportingName}</Text>
                    <Text>Subscription product: {planValues.subscriptionProduct}</Text>
                    <Text>Promo affiliate name: {planValues.affiliateName}</Text>
                    <Text className="description-text">Description: {planValues.description}</Text>
                </Column>

                <Row alignmentHorizontal="justify">
                    <Column spacing="200">
                        <Text tag="label" type="h100">
                            Employee plan
                        </Text>
                        {planValues.employeePlan ? planOnIconWithLabel : planOffIconWithLabel}
                    </Column>

                    <Column spacing="200">
                        <Text tag="label" type="h100">
                            Carrier plan
                        </Text>
                        {planValues.carrierPlan ? planOnIconWithLabel : planOffIconWithLabel}
                    </Column>

                    <Column spacing="200">
                        <Text tag="label" type="h100">
                            Royalty bearing
                        </Text>
                        {planValues.royaltyBearing ? planOnIconWithLabel : planOffIconWithLabel}
                    </Column>

                    <Column spacing="200">
                        <Text tag="label" type="h100">
                            Customer pays
                        </Text>
                        {planValues.customerPays ? planOnIconWithLabel : planOffIconWithLabel}
                    </Column>
                </Row>

                <Select
                    label="Plan type *"
                    value={planValues.planType}
                    onChange={(newVal) => handlePlanValuesChange('planType', newVal)}
                    onBlur={() => onBlurField('planType')}
                    error={!!errorMessages.planType}
                    errorMessage={errorMessages.planType}
                    size="xlarge"
                >
                    {PLAN_TYPE_OPTIONS.map((option) => (
                        <SelectOption label={option} value={option} key={option} />
                    ))}
                </Select>

                {!planValues.carrierPlan ? (
                    <Column spacing="200">
                        <Text tag="label" type="h100">
                            Territory
                        </Text>
                        <Text>{GLOBAL_TERRITORY.NAME}</Text>
                    </Column>
                ) : (
                    <Select
                        label="Territory *"
                        value={planValues.territory}
                        onChange={(newVal) => handlePlanValuesChange('territory', newVal)}
                        onBlur={() => onBlurField('territory')}
                        error={!!errorMessages.territory}
                        errorMessage={errorMessages.territory}
                        size="xlarge"
                    >
                        {territories.map((territory) => (
                            <SelectOption
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                value={territory.territoryCode!}
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                label={territory.territoryCode!}
                                key={territory.territoryId}
                            >
                                {() => <TerritoryFlag territoryCode={territory.territoryCode!} />}
                            </SelectOption>
                        ))}
                    </Select>
                )}

                <Row alignmentHorizontal="justify">
                    <DatePicker
                        width="265px"
                        locale="en-CA"
                        value={planValues.startDate}
                        onChange={(newVal) => handlePlanValuesChange('startDate', newVal)}
                        onBlur={() => onBlurField('startDate')}
                        error={!!errorMessages.startDate}
                        errorMessage={errorMessages.startDate}
                        label="Start date *"
                        size="xlarge"
                    />
                    <DatePicker
                        width="265px"
                        locale="en-CA"
                        value={planValues.endDate}
                        onChange={(newVal) => handlePlanValuesChange('endDate', newVal)}
                        onBlur={() => onBlurField('endDate')}
                        error={!!errorMessages.endDate}
                        errorMessage={errorMessages.endDate}
                        label="End date"
                        size="xlarge"
                    />
                </Row>

                <Row className="radio-select-row">
                    <RadioButton
                        name="isRenewalCadenceChecked"
                        checked={planValues.isRenewalCadenceChecked}
                        onChange={() => handlePlanValuesChange('isRenewalCadenceChecked', true)}
                    />

                    <Select
                        label="Renewal cadence *"
                        disabled={!planValues.isRenewalCadenceChecked}
                        value={planValues.renewalCadence}
                        onChange={(newVal) => handlePlanValuesChange('renewalCadence', newVal)}
                        onBlur={() => onBlurField('renewalCadence')}
                        error={!!errorMessages.renewalCadence}
                        errorMessage={errorMessages.renewalCadence}
                        size="xlarge"
                    >
                        {RENEWAL_CADENCE_OPTIONS.map((option) => (
                            <SelectOption label={option} value={option} key={option} />
                        ))}
                    </Select>
                </Row>

                <Row className="radio-select-row">
                    <RadioButton
                        name="isRenewalCadenceChecked"
                        checked={!planValues.isRenewalCadenceChecked}
                        onChange={() => handlePlanValuesChange('isRenewalCadenceChecked', false)}
                    />

                    <Select
                        label="Promo duration *"
                        disabled={planValues.isRenewalCadenceChecked}
                        value={planValues.promoDurationInDays}
                        onChange={(newVal) => handlePlanValuesChange('promoDurationInDays', newVal)}
                        onBlur={() => onBlurField('promoDurationInDays')}
                        error={!!errorMessages.promoDuration}
                        errorMessage={errorMessages.promoDuration}
                        size="xlarge"
                    >
                        {PROMO_DURATION_OPTIONS.map((option) => (
                            <SelectOption label={option.slice(0, -2) + ' Days'} value={option} key={option} />
                        ))}
                    </Select>
                </Row>

                {showPriceInputGroup && (
                    <>
                        <InputGroup size="xlarge">
                            <Input
                                label="Wholesale price"
                                value={planValues.wholesalePrice}
                                onChange={(newVal) => handlePlanValuesChange('wholesalePrice', newVal)}
                                onBlur={() => onBlurField('wholesalePrice')}
                                error={!!errorMessages.wholesalePrice}
                                errorMessage={errorMessages.wholesalePrice}
                                type="number"
                                size="xlarge"
                            />
                            <Select
                                data-testid="wholesalePriceCurrencySelect"
                                width="83px"
                                value={planValues.wholesalePriceCurrency}
                                onChange={(newVal) => handlePlanValuesChange('wholesalePriceCurrency', newVal)}
                                onBlur={() => onBlurField('wholesalePriceCurrency')}
                                error={!!errorMessages.wholesalePriceCurrency}
                                errorMessage={errorMessages.wholesalePriceCurrency}
                            >
                                {uniqBy(territories, 'currency').map((territory) => (
                                    <SelectOption
                                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                        value={territory.currency!}
                                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                        label={territory.currency!}
                                        key={territory.territoryId}
                                    />
                                ))}
                            </Select>
                        </InputGroup>

                        <InputGroup size="xlarge">
                            <Input
                                label="End user price"
                                value={planValues.endUserPrice}
                                onChange={(newVal) => handlePlanValuesChange('endUserPrice', newVal)}
                                onBlur={() => onBlurField('endUserPrice')}
                                error={!!errorMessages.endUserPrice}
                                errorMessage={errorMessages.endUserPrice}
                                type="number"
                                size="xlarge"
                            />
                            <Select
                                data-testid="endUserPriceCurrencySelect"
                                width="83px"
                                value={planValues.endUserPriceCurrency}
                                onChange={(newVal) => handlePlanValuesChange('endUserPriceCurrency', newVal)}
                                onBlur={() => onBlurField('endUserPriceCurrency')}
                                error={!!errorMessages.endUserPriceCurrency}
                                errorMessage={errorMessages.endUserPriceCurrency}
                            >
                                {uniqBy(territories, 'currency').map((territory) => (
                                    <SelectOption
                                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                        value={territory.currency!}
                                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                        label={territory.currency!}
                                        key={territory.territoryId}
                                    />
                                ))}
                            </Select>
                        </InputGroup>
                    </>
                )}
                {showPromoMultiplierInput && (
                    <Input
                        label="Promo multiplier"
                        value={planValues.promoMultiplier}
                        onChange={(newVal) => handlePlanValuesChange('promoMultiplier', newVal)}
                        onBlur={() => onBlurField('promoMultiplier')}
                        error={!!errorMessages.promoMultiplier}
                        errorMessage={errorMessages.promoMultiplier}
                        type="number"
                        size="xlarge"
                    />
                )}
            </Column>

            <Column spacingInset="500 none none none">
                <Row alignmentHorizontal="end">
                    <Button type="link" onClick={onCancel} aria-label="Cancel plan">
                        Cancel
                    </Button>
                    <Button type="tertiary" onClick={onPrevious} aria-label="Go to previous step">
                        Previous
                    </Button>
                    <Button type="primary" disabled={!isFormValid} onClick={onNext} aria-label="Go to next step">
                        Next
                    </Button>
                </Row>
            </Column>
        </>
    );
};
